import React from "react";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Link from "components/link";
import IconButton from "components/icon-button";
import { injectIntl, FormattedMessage } from "react-intl";
import Icon from "components/icon";
import { ApiConnectedPaginatedTable, TableIconCell, CustomTableRow } from "components/table";
import styles from "./styles.css.js";
import withStyles from "components/with-styles";
import cn from "classnames";
import OptionalDate from "components/optional-date";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/react-hooks";
import mutation from "./mutation";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { DeleteActionWithModal } from "components/buttons";
import { useAuthDataContext } from "components/auth";
import { getSourcesService } from "../../../services/streamsourceServices";
import { apiPaginationConverter } from "../../../utils/api-pagination";
import { Restricted, permissions } from "components/auth";
import { SecondCoachButtonWithModal } from "components/buttons";
import RemoveSourceButton from "./partial";
import { TextDisplay } from "components/display-data";
import { GENERAL_STATUS_KO } from "../../../utils/constants";

const Header = () => <TableRow />;

const RowComponent = ({
  classes,
  intl,
  item,
  refetchData,
  showDeployment,
  showDeploymentAssociationControls = false,
}) => {
  const [deleteSource] = useMutation(mutation);
  const { currentCustomerId, deployments } = useCurrentCustomerContext();

  const deployment = deployments?.find(d => d._id === item?.deployment?.id);

  const isKo = item.lastStatus === GENERAL_STATUS_KO;
  const detailsUrl = `/source/details/${item.id}`;
  const viewerUrl = `/source/viewer/${item.id}`;

  return (
    <CustomTableRow key={item.id} href={detailsUrl}>
      {showDeploymentAssociationControls && (
        <Restricted permissions={[permissions.source_manage, permissions.deployment_manage]}>
          <TableIconCell>
            <RemoveSourceButton show={showDeploymentAssociationControls} item={item} />
          </TableIconCell>
        </Restricted>
      )}
      {showDeployment && (
        <TableCell>
          {deployment ? (
            <TextDisplay label={intl.formatMessage({ id: "source.deployment" })}>
              <Link color="primary" href={`/deployment/details/${deployment._id}`}>
                {deployment.codename}
              </Link>
            </TextDisplay>
          ) : (
            <TextDisplay label={intl.formatMessage({ id: "source.deployment" })}>-</TextDisplay>
          )}
        </TableCell>
      )}
      <TableCell>
        <div>
          <OptionalDate value={item.lastChecked} />
        </div>
        <div>{item.description}</div>
      </TableCell>
      <TableCell>
        <div className={classes.title}>{item.brand}</div>
        <div className={classes.code}>{item.model}</div>
        <div>
          <FormattedMessage id="source.serial" />
          <div className={classes.code}>{item.serial}</div>
        </div>
      </TableCell>
      <TableCell>
        <div>
          <TextDisplay label={intl.formatMessage({ id: "common.type" })}>{item.type}</TextDisplay>
        </div>
        <div>
          <TextDisplay label={intl.formatMessage({ id: "source.use" })}>{item.use}</TextDisplay>
        </div>
        <div>
          <TextDisplay label={intl.formatMessage({ id: "source.owner" })}>{item.owner}</TextDisplay>
        </div>
      </TableCell>
      <TableCell>
        <TextDisplay label={intl.formatMessage({ id: "source.lastStatus" })}>
          <span className={cn({ [classes.statusKo]: isKo })}>{item.lastStatus || "-"}</span>
        </TextDisplay>
      </TableCell>
      <TableIconCell>
        <Link href={detailsUrl} target="_blank" rel="noopener">
          <IconButton>
            <Icon name="launch" />
          </IconButton>
        </Link>
        <Link href={viewerUrl}>
          <IconButton>
            <Icon name="pageview" />
          </IconButton>
        </Link>
        {item.publicInterface && item.publicInterface.secondCoachUrl && (
          <SecondCoachButtonWithModal onlyIcon secondCoachUrl={item.publicInterface.secondCoachUrl} />
        )}
        <Restricted permissions={[permissions.source_manage]}>
          <Link href={`/source/edit/${item.id}`}>
            <IconButton className={classes.iconButton}>
              <Icon name="edit" />
            </IconButton>
          </Link>
          <DeleteActionWithModal
            mutation={deleteSource}
            mutationVariables={{ sourceId: item.id, customerId: currentCustomerId }}
            elementNameInResult="deleteSource"
            elementToDelete={item.description}
            refetchDataOnSuccess
            refetchData={refetchData}
          />
        </Restricted>
      </TableIconCell>
    </CustomTableRow>
  );
};

RowComponent.propTypes = {
  /**
   ** Indica si muestra un enlace a la instalación
   **/
  showDeployment: PropTypes.bool,
  /**
   ** Elemento a representar en la fila de la tabla
   **/
  item: PropTypes.object,
  /**
   * Función de refetch de datos
   */
  refetchData: PropTypes.func.isRequired,
  /**
   ** Indica si se muestran los controles de borrar la asociación con el deployment
   **/
  showDeploymentAssociationControls: PropTypes.bool,
};

const I18nStyledRowComponent = injectIntl(withStyles(styles)(RowComponent));

const SourcePaginatedTable = ({
  variables,
  showDeployment = false,
  showDeploymentAssociationControls = false,
  page = 0,
  ...props
}) => {
  const { hasAdminPrivileges, user } = useAuthDataContext();
  const { currentCustomerId } = useCurrentCustomerContext();

  const APIPetition = (params, callback) => {
    const { page: pageNum, perPage } = params;
    const { offset, limit } = apiPaginationConverter(pageNum, perPage);

    return getSourcesService({
      offset,
      limit,
      deploymentId: variables?.deploymentId,
      deploymentIds: user?.deploymentIds,
      partnerId: variables?.partnerId,
      hasAdminPrivileges,
      contractIds: variables?.contractIds,
      status: variables?.status,
      withoutDeployment: variables?.withoutDeployment,
      usuallyDisconnected: variables?.usuallyDisconnected,
      customerId: hasAdminPrivileges ? variables?.customerId : currentCustomerId,
    })
      .then(callback)
      .catch(error => {
        console.error("Error fetching sources:", error);
        throw error;
      });
  };

  return (
    <ApiConnectedPaginatedTable
      APIPetition={APIPetition}
      variables={variables}
      headerComponent={Header}
      rowComponent={I18nStyledRowComponent}
      rowProps={{ showDeployment, showDeploymentAssociationControls }}
      paginatedElementsHandler={data => data}
      page={page}
      isUrlPagination
      {...props}
    />
  );
};

SourcePaginatedTable.propTypes = {
  /**
   ** Objeto que contiene los filtrados de la búsqueda
   **/
  variables: PropTypes.object,
  /**
   ** Callback que se llama si la tabla está vacía
   **/
  onEmpty: PropTypes.func,
  /**
   ** Número de página a mostrar
   **/
  page: PropTypes.number,
};

export const DeploymentSourcePaginatedTable = ({ ...props }) => (
  <SourcePaginatedTable showDeploymentAssociationControls {...props} />
);

DeploymentSourcePaginatedTable.propTypes = {
  ...SourcePaginatedTable.propTypes,
};

export const AlarmsPaginatedTable = ({ variables, onEmpty, ...props }) => (
  <SourcePaginatedTable
    showDeployment
    onEmpty={onEmpty}
    variables={{ ...variables, status: GENERAL_STATUS_KO, usuallyDisconnected: false }}
    {...props}
  />
);

AlarmsPaginatedTable.propTypes = {
  /**
   ** Objeto que contiene los filtrados de la búsqueda
   **/
  variables: PropTypes.object,
  /**
   ** Callback que se llama cuando la tabla está vacía
   **/
  onEmpty: PropTypes.func,
  /**
   ** Página a cargar en la tabla
   **/
  page: PropTypes.number,
};

export default SourcePaginatedTable;
